import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { ResultRow, handleRowKeyNav } from './ResultRow';
import { Highlight } from './Highlight';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Checkbox } from '../ui/Checkbox';
import { cx } from '../../lib/cx';

const PAGE = 100;

/** Renders rows in pages of 100 so very broad searches stay responsive. */
const usePaged = (items, resetKey) => {
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => { setLimit(PAGE); }, [resetKey]);
  return {
    visible: items.slice(0, limit),
    remaining: Math.max(0, items.length - limit),
    showMore: () => setLimit((l) => l + PAGE),
  };
};

const ShowMore = ({ remaining, onClick }) => (remaining > 0 ? (
  <div className="flex justify-center border-t border-line p-3">
    <Button variant="secondary" size="sm" onClick={onClick}>
      Show {Math.min(PAGE, remaining)} more ({remaining.toLocaleString()} hidden)
    </Button>
  </div>
) : null);

/** Flat list, best matches first. */
export const ResultList = ({ models, highlighter, selectedId, onOpen, resetKey, followFocus = false }) => {
  const { visible, remaining, showMore } = usePaged(models, resetKey);
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface">
      <ul aria-label="Results" onKeyDown={(e) => handleRowKeyNav(e, followFocus ? onOpen : null)}>
        {visible.map((m) => (
          <ResultRow key={m.id} model={m} highlighter={highlighter} selected={m.id === selectedId} onOpen={onOpen} />
        ))}
      </ul>
      <ShowMore remaining={remaining} onClick={showMore} />
    </div>
  );
};

const LabGroup = ({ lab, models, highlighter, selectedId, onOpen, open, onToggle, homepage, followFocus }) => {
  const { visible, remaining, showMore } = usePaged(models, lab);
  const headingId = `lab-${lab.replace(/[^a-z0-9]+/gi, '-')}`;
  return (
    <section aria-labelledby={headingId} className="overflow-hidden rounded-xl border border-line bg-surface">
      <div className="flex items-center gap-2 bg-sunken/60 pr-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2.5 px-4 py-2.5 text-left"
        >
          <ChevronDown size={15} className={cx('shrink-0 text-faint transition-transform', !open && '-rotate-90')} aria-hidden="true" />
          <h3 id={headingId} className="truncate text-sm font-semibold text-fg">{lab.replace(/\s*-\s*Custom Databases$/, ' (custom)')}</h3>
          <span className="shrink-0 text-xs tabular-nums text-muted">
            {models.length.toLocaleString()} {models.length === 1 ? 'test' : 'tests'}
          </span>
        </button>
        {homepage && (
          <a
            href={homepage}
            target="_blank"
            rel="noopener noreferrer"
            referrerPolicy="no-referrer"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted hover:text-accent-fg"
          >
            Handbook <ExternalLink size={12} aria-hidden="true" />
            <span className="sr-only">(opens {lab} test handbook in a new tab)</span>
          </a>
        )}
      </div>
      {open && (
        <>
          <ul onKeyDown={(e) => handleRowKeyNav(e, followFocus ? onOpen : null)} className="border-t border-line">
            {visible.map((m) => (
              <ResultRow
                key={m.id}
                model={m}
                highlighter={highlighter}
                selected={m.id === selectedId}
                onOpen={onOpen}
                showLab={false}
              />
            ))}
          </ul>
          <ShowMore remaining={remaining} onClick={showMore} />
        </>
      )}
    </section>
  );
};

/** Results grouped by lab (alphabetical), every group expanded by default. */
export const GroupedResults = ({ models, highlighter, selectedId, onOpen, labHomepages, resetKey, followFocus = false }) => {
  const groups = useMemo(() => {
    const byLab = new Map();
    models.forEach((m) => {
      if (!byLab.has(m.labGroup)) byLab.set(m.labGroup, []);
      byLab.get(m.labGroup).push(m);
    });
    return Array.from(byLab).sort(([a], [b]) => a.localeCompare(b));
  }, [models]);

  const [collapsed, setCollapsed] = useState(() => new Set());
  useEffect(() => { setCollapsed(new Set()); }, [resetKey]);

  const toggle = (lab) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(lab)) next.delete(lab);
    else next.add(lab);
    return next;
  });

  const allCollapsed = groups.length > 0 && collapsed.size >= groups.length;

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(groups.map(([lab]) => lab)))}
        >
          {allCollapsed ? 'Expand all' : 'Collapse all'}
        </Button>
      </div>
      {groups.map(([lab, labModels]) => (
        <LabGroup
          key={lab}
          lab={lab}
          models={labModels}
          highlighter={highlighter}
          selectedId={selectedId}
          onOpen={onOpen}
          open={!collapsed.has(lab)}
          onToggle={() => toggle(lab)}
          homepage={labHomepages[lab]}
          followFocus={followFocus}
        />
      ))}
    </div>
  );
};

export const COMPARE_COLUMNS = [
  ['specimen', 'Specimen'],
  ['container', 'Container'],
  ['volume', 'Volume'],
  ['stability', 'Stability & storage'],
  ['transport', 'Transport'],
  ['turnaround', 'Turnaround'],
  ['referredTo', 'Referred to'],
];

/**
 * Side-by-side comparison: one row per result, key facts as columns.
 * Defaults to the closest matches (exact title or synonym) when there are several.
 */
export const CompareTable = ({ models, highlighter, selectedId, onOpen, resetKey }) => {
  const closest = useMemo(() => models.filter((m) => m.rank <= 1), [models]);
  const [closestOnly, setClosestOnly] = useState(true);
  useEffect(() => { setClosestOnly(true); }, [resetKey]);

  const useClosest = closestOnly && closest.length >= 2;
  const rows = useClosest ? closest : models;
  const columns = COMPARE_COLUMNS.filter(([key]) => rows.some((m) => m.facts[key]));
  const { visible, remaining, showMore } = usePaged(rows, `${resetKey}|${useClosest}`);

  return (
    <div className="space-y-2">
      {closest.length >= 2 && closest.length < models.length && (
        <label className="flex w-fit cursor-pointer items-center gap-2 text-sm text-fg-2">
          <Checkbox checked={closestOnly} onChange={setClosestOnly} />
          Closest matches only
          <span className="text-muted">({closest.length} of {models.length})</span>
        </label>
      )}
      <div className="overflow-x-auto rounded-xl border border-line bg-surface scrollbar-thin">
        <table className="w-full min-w-[56rem] border-collapse text-left text-sm">
          <caption className="sr-only">Comparison of key collection details by lab</caption>
          <thead className="bg-sunken/70 text-xs text-muted">
            <tr>
              <th scope="col" className="sticky left-0 z-10 bg-sunken px-4 py-2.5 font-medium">Test &amp; lab</th>
              {columns.map(([key, label]) => (
                <th key={key} scope="col" className="px-3 py-2.5 font-medium">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody onKeyDown={(e) => handleRowKeyNav(e)}>
            {visible.map((m) => (
              <tr
                key={m.id}
                className={cx(
                  'cursor-pointer border-t border-line align-top transition-colors',
                  m.id === selectedId ? 'bg-accent/10' : 'hover:bg-raised/50',
                )}
                onClick={() => onOpen(m.id)}
              >
                <th scope="row" className={cx('sticky left-0 z-[1] max-w-[16rem] px-4 py-2.5 font-normal', m.id === selectedId ? 'bg-accent/10' : 'bg-surface')}>
                  <button
                    type="button"
                    data-row-button
                    data-result-id={m.id}
                    onClick={(e) => { e.stopPropagation(); onOpen(m.id); }}
                    className="text-left font-medium text-fg hover:text-accent-fg"
                  >
                    <Highlight text={m.titleText} highlighter={highlighter} />
                  </button>
                  <span className="mt-0.5 block text-xs text-muted">
                    {[m.labName, m.subLab].filter(Boolean).join(' · ')}
                  </span>
                  {m.referral === 'referred' && <Badge tone="accent" className="mt-1">Referred</Badge>}
                </th>
                {columns.map(([key]) => (
                  <td key={key} className="max-w-[14rem] px-3 py-2.5 text-fg-2">
                    {m.facts[key]
                      ? <span className="line-clamp-4"><Highlight text={m.facts[key]} highlighter={highlighter} /></span>
                      : <span className="text-faint" aria-label="not stated">—</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <ShowMore remaining={remaining} onClick={showMore} />
      </div>
    </div>
  );
};
